import captureError from '@disco/utils/captureError'

import type { JsonNotification, PongMessage } from 'types/notification'

import api from '@disco/api/server'
import retry from 'retry'

import { __REACT_NATIVE__ } from '@disco/constants/environment'

/**
 * Processes unknown WebSocket notification formats and logs errors.
 *
 * Captures errors when receiving WebSocket messages that don't match
 * expected notification formats for debugging purposes.
 *
 * @param notification - The unknown notification object received
 *
 * @example
 * ```typescript
 * const unknownMessage = { type: 'unknown', data: 'unexpected' }
 * processUnknownNotification(unknownMessage)
 * // Logs error with notification details for debugging
 * ```
 */
export const processUnknownNotification = (notification: any) => {
  const unknownFormatError = new Error('Unknown websocket message')
  captureError(unknownFormatError, { extra: { notification } })
}

const MESSAGE_RESEND_TIME = 5000
const KEEPALIVE_INTERVAL = 15000
const PONG_TIMEOUT = 10000
const MAX_HISTORY_SIZE = 1000

const WS_RECONNECT_OPTIONS = {
  forever: true,
  factor: 2,
  minTimeout: 10000,
  maxTimeout: 3 * 60 * 1000,
  randomize: true,
}

/**
 * WebSocket client for real-time communication with the server.
 *
 * Handles connection management, automatic reconnection, keep-alive pings,
 * message handling, and notification processing. Supports both web and
 * React Native environments with appropriate fallbacks.
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Keep-alive mechanism to detect connection issues
 * - Message deduplication to prevent duplicate notifications
 * - Token-based authentication
 * - Bulk notification handling for missed messages
 */
class WebsocketsClient {
  socket: WebSocket | null | undefined
  token: string | null | undefined
  interval: ReturnType<typeof setInterval> | null | undefined
  reconnectInterval: ReturnType<typeof setInterval> | null | undefined
  keepaliveTimestamp: number | null | undefined
  pongTimestamp: number
  lastMessageTime: number | null | undefined
  storedMessageIds: Array<string>
  wsHeaders: any
  reconnectAttempts: number
  notificationHandler: (notification?: any) => void | null | undefined
  bulkNotificationsHandler: (notifications?: any) => void | null | undefined
  allowReconnect: boolean

  constructor() {
    this.socket = null
    this.token = null
    this.interval = null
    this.reconnectInterval = null
    this.reconnectAttempts = 0
    this.keepaliveTimestamp = null
    this.pongTimestamp = 0
    this.lastMessageTime = null
    this.storedMessageIds = []
    this.wsHeaders = null
    this.allowReconnect = true
  }

  /**
   * Sets the notification handlers for processing incoming messages.
   *
   * @param handlers - Object containing notification handler functions
   * @param handlers.notificationHandler - Function to handle individual notifications
   * @param handlers.bulkNotificationsHandler - Function to handle bulk notifications
   *
   * @example
   * ```typescript
   * wsClient.setHandlers({
   *   notificationHandler: (notification) => {
   *     updateUI(notification)
   *   },
   *   bulkNotificationsHandler: (notifications) => {
   *     notifications.forEach(updateUI)
   *   }
   * })
   * ```
   */
  setHandlers: (handlers: {
    notificationHandler: any
    bulkNotificationsHandler: any
  }) => void = handlers => {
    this.notificationHandler = handlers.notificationHandler
    this.bulkNotificationsHandler = handlers.bulkNotificationsHandler
  }

  /**
   * Creates a WebSocket instance with platform-specific configuration.
   *
   * Handles differences between web and React Native WebSocket implementations,
   * including custom headers for mobile authentication.
   *
   * @param url - WebSocket URL to connect to
   * @returns WebSocket instance configured for the current platform
   *
   * @example
   * ```typescript
   * const ws = wsClient.createWebSocket('wss://api.example.com/ws')
   * ```
   */
  createWebSocket: (url: string) => WebSocket = url => {
    if (__REACT_NATIVE__) {
      // @ts-expect-error todo fix later
      return new WebSocket(url, null, {
        headers: this.wsHeaders,
      })
    }
    return new WebSocket(url)
  }

  /**
   * Opens a WebSocket connection to the server.
   *
   * Retrieves connection parameters from the API, handles missed messages,
   * and sets up event listeners. Includes automatic retry logic for
   * connection failures.
   *
   * @returns Promise that resolves when connection is established
   *
   * @example
   * ```typescript
   * try {
   *   await wsClient.openConnection()
   *   console.log('Connected to WebSocket')
   * } catch (error) {
   *   console.error('Failed to connect:', error)
   * }
   * ```
   */
  openConnection: () => Promise<void> = async () => {
    const { reconnectInterval, socket, lastMessageTime } = this
    console.log('open connection', reconnectInterval, socket, lastMessageTime)

    if (__REACT_NATIVE__ && !api.deviceToken) {
      // device token is required for websocket connection
      // wait for device token to be set in NotificationController
      this.reconnectToWebsocket()
      return
    }

    if (reconnectInterval) clearInterval(reconnectInterval)

    if (socket) return

    const websocketRequestParams = lastMessageTime
      ? {
          retrieve_messages_after: lastMessageTime,
        }
      : {}

    try {
      const result = await api.getWebsocketParams(websocketRequestParams)

      const { token: resToken, url, messages, timestamp } = result
      this.token = resToken

      this.lastMessageTime = timestamp
      if (messages && messages.length > 0 && this.bulkNotificationsHandler) {
        this.bulkNotificationsHandler(messages)
      }
      const websocket = await this.createWebSocket(url)

      websocket.addEventListener('open', this.socketOpenEventHandler)
      websocket.addEventListener('error', this.socketErrorHandler)
      websocket.addEventListener('message', this.socketMessageHandler)

      this.socket = websocket

      // Disabled due to large number of events(300-400k per day) affecting our quota on Mixpanel
      // const intevalDown = this._lastMessageTime
      //   ? (new Date() - this._lastMessageTime) / 1000
      //   : null

      // if (intevalDown) {
      //   mixpanel.sendPureEvent('Re-open WebSocket connection', {
      //     'Time since last message': intevalDown,
      //   })
      // }
    } catch (error: any) {
      console.log('websocket error', error)
      captureError(error)

      if (error.status !== 403 && error.name !== 'SyntaxError') {
        this.reconnectAttempts++
        this.reconnectToWebsocket()
      } else {
        this.closeConnection()
        this.reconnectAttempts = 0
      }
    }
  }

  /**
   * Closes the WebSocket connection and cleans up resources.
   *
   * Removes event listeners, clears intervals, and resets connection state.
   * Safe to call multiple times.
   *
   * @example
   * ```typescript
   * wsClient.closeConnection()
   * console.log('WebSocket connection closed')
   * ```
   */
  closeConnection: () => void = () => {
    const { socket, interval } = this

    if (interval) clearInterval(interval)

    if (socket) {
      console.log('close connection', socket, interval)
      socket.close()
      socket.removeEventListener('open', this.socketOpenEventHandler)
      socket.removeEventListener('error', this.socketErrorHandler)
      socket.removeEventListener('message', this.socketMessageHandler)
    }

    this.socket = null
    this.interval = null
    this.pongTimestamp = 0
    this.keepaliveTimestamp = 0
  }

  /**
   * Sends a message through the WebSocket connection.
   *
   * Checks connection state before sending and triggers reconnection
   * if the socket is not ready.
   *
   * @param message - String message to send
   *
   * @example
   * ```typescript
   * wsClient.sendSocketMessage('{"type": "ping", "timestamp": 1234567890}')
   * ```
   */
  sendSocketMessage: (message: string) => void = message => {
    console.log('send socket message', message)
    const socket = this.socket
    if (socket && socket.readyState === 1) {
      socket.send(message)
    } else {
      console.log('socket not ready, message not sent', message)
      this.reconnectToWebsocket()
    }
  }

  /**
   * Initiates WebSocket reconnection with exponential backoff.
   *
   * Closes current connection and schedules reconnection attempt
   * based on the number of previous failed attempts.
   *
   * @example
   * ```typescript
   * // Manually trigger reconnection
   * wsClient.reconnectToWebsocket()
   * ```
   */
  reconnectToWebsocket: (reconnectImmediately?: boolean) => void = (reconnectImmediately = false) => {
    console.log('reconnect to websocket', reconnectImmediately)
    this.closeConnection()

    if (!this.allowReconnect) {
      return
    }

    if (reconnectImmediately) {
      this.openConnection()
      return
    }

    const reconnectTimeout = retry.createTimeout(
      this.reconnectAttempts,
      WS_RECONNECT_OPTIONS,
    )

    // on error, try to reconnect after timeout, ensure that
    // reconnect interval is cleared before setting new one
    if (this.reconnectInterval) clearInterval(this.reconnectInterval)
    this.reconnectInterval = setInterval(() => {
      this.openConnection()
    }, reconnectTimeout)
  }

  /**
   * Processes pong response from server for keep-alive mechanism.
   *
   * Updates the pong timestamp to track connection health.
   * Used internally by the keep-alive system.
   *
   * @param timestamp - Timestamp from the pong response
   *
   * @example
   * ```typescript
   * // Called automatically when pong message received
   * wsClient.processPong(Date.now())
   * ```
   */
  processPong: (arg1: number) => void = timestamp => {
    this.pongTimestamp = timestamp
  }

  /**
   * Checks connection state and triggers reconnection if needed.
   *
   * Monitors WebSocket ready state and initiates reconnection
   * for closed or failed connections.
   *
   * @example
   * ```typescript
   * // Check connection health
   * wsClient.updateConnection()
   * ```
   */
  updateConnection: (reconnectImmediately?: boolean) => void = (reconnectImmediately: boolean = false) => {
    // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
    const { socket, reconnectToWebsocket } = this
    const readyState = socket?.readyState ?? 3
    if (readyState > 1) reconnectToWebsocket(reconnectImmediately)
  }

  socketOpenEventHandler: () => void = () => {
    console.log('socket open', new Date())
    // sometimes onopen event fired when socket still in CONNECTING state
    // in this case - send token again with timeout
    try {
      if (this.token) this.sendSocketMessage(this.token)
    } catch (e: any) {
      captureError(e)
      setTimeout(() => {
        if (this.token) this.sendSocketMessage(this.token)
      }, MESSAGE_RESEND_TIME)
    }
    this.setPingInterval(() => {
      // TODO: move to datadog or delete? Sentry.metrics.increment('websocket_invalidated', 1)
    })
  }

  setPingInterval: (reconnectErrorHandler: () => void) => void =
    reconnectErrorHandler => {
      const { interval, socket } = this
      // ensure that interval is cleared after reconnect
      if (interval) clearInterval(interval)

      this.interval = setInterval(() => {
        // send keepalive messages
        if (socket) {
          const keepalive = new Date().getTime()
          this.sendSocketMessage(JSON.stringify({ keepalive }))
          this.keepaliveTimestamp = keepalive
          setTimeout(
            () => this.checkConnection(reconnectErrorHandler),
            PONG_TIMEOUT,
          )
        } else {
          if (interval) clearInterval(interval)
        }
      }, KEEPALIVE_INTERVAL)
    }

  checkConnection: (reconnectErrorHandler: () => void) => void =
    reconnectErrorHandler => {
      const { keepaliveTimestamp, pongTimestamp } = this
      if (
        keepaliveTimestamp &&
        pongTimestamp &&
        (keepaliveTimestamp > pongTimestamp ||
          pongTimestamp - keepaliveTimestamp >= PONG_TIMEOUT)
      ) {
        this.reconnectToWebsocket()
        reconnectErrorHandler()
      }
    }

  socketErrorHandler: (e?: any) => void = (e: any) => {
    console.log('_socketErrorHandler', e)

    if (e.status !== 403 && !(e.message ?? '').includes('403')) {
      // TODO: Improve error handling with refactor
      // TODO: move to datadog or delete? Sentry.metrics.increment('websocket_error', 1)
      this.reconnectAttempts++
      this.reconnectToWebsocket()
    }
  }

  socketMessageHandler: (e: MessageEvent) => void = e => {
    this.reconnectAttempts = 0
    if (e.data === 'authed') console.log('authed', new Date())
    if (e.data === 'authed') return
    if (e.data === 'token expired') {
      this.closeConnection()
      this.openConnection()
      return
    }

    if (typeof e.data === 'string') {
      try {
        const notification = JSON.parse(e.data)
        console.log(`${new Date().toISOString()}`, notification)
        const timestamp = notification.message_timestamp
        if (timestamp) {
          this.lastMessageTime = timestamp
        }

        const messageId = notification.message_id

        // Check if message ID is already stored
        if (
          messageId &&
          this.storedMessageIds.length > 0 &&
          this.storedMessageIds.includes(messageId)
        ) {
          // Message already processed, return to avoid re-processing
          return
        }

        // Store new or unprocessed message, add its ID to the processed array
        if (messageId) {
          // Ensure the array stays within max history size items by removing the oldest one
          this.storedMessageIds = [messageId, ...this.storedMessageIds].slice(
            0,
            MAX_HISTORY_SIZE,
          )
        }

        this.processNotification(notification)
        // this._sendMessageConfirmation(notification)
      } catch (parseError: any) {
        const data =
          typeof e.data === 'string'
            ? e.data.substring(0, Math.min(e.data.length, 500))
            : e.data
        captureError(parseError, { extra: { data } })
      }
    } else {
      captureError(new Error('Unknown Websocket message type'), {
        extra: { data: e.data },
      })
    }
  }

  processNotification: (notification: JsonNotification | PongMessage) => void =
    notification => {
      if (notification && (notification as PongMessage).pong) {
        // @ts-expect-error todo fix later
        return this.processPong(parseInt((notification as PongMessage).pong))
      }
      if (
        notification &&
        // @ts-expect-error todo fix later
        notification.error &&
        // @ts-expect-error todo fix later
        notification.error === 'connection was closed'
      )
        return this.reconnectToWebsocket()

      if (this.notificationHandler) this.notificationHandler(notification)
    }

  /**
   * Sets the allowReconnect flag.
   *
   * @param allowReconnect - The allowReconnect flag
   *
   * @example
   * ```typescript
   * wsClient.setAllowReconnect(false)
   */
  setAllowReconnect: (allowReconnect: boolean) => void = allowReconnect => {
    this.allowReconnect = allowReconnect
  }

  // backend doesn't acknowledge message delivery, so we don't need to
  // send this
  /*
  _sendMessageConfirmation = (message: *) => {
    const { message_id } = message
    if (message_id)
      this._sendSocketMessage(JSON.stringify({ message_id, type: 'ack' }))
  }
  */
}

class WebsocketsClientInstance {
  static #instance: WebsocketsClient

  constructor() {}

  static getInstance() {
    if (!WebsocketsClientInstance.#instance) {
      WebsocketsClientInstance.#instance = new WebsocketsClient()
    }
    return WebsocketsClientInstance.#instance
  }
}

/**
 * Singleton instance of the WebSocket client.
 *
 * Global WebSocket client instance for real-time communication throughout
 * the application. Handles automatic reconnection, message processing,
 * and notification delivery.
 *
 * @example
 * ```typescript
 * // Set up notification handlers
 * websockets.setHandlers({
 *   notificationHandler: (notification) => {
 *     console.log('Received notification:', notification)
 *   },
 *   bulkNotificationsHandler: (notifications) => {
 *     console.log('Received bulk notifications:', notifications.length)
 *   }
 * })
 *
 * // Connect to WebSocket
 * await websockets.openConnection()
 *
 * // Close connection when done
 * websockets.closeConnection()
 * ```
 */
export const websockets = WebsocketsClientInstance.getInstance()
